import React, { useCallback, useEffect, useRef, useState } from 'react';

function getRelativeCoords(event, target) {
  const rect = target.getBoundingClientRect();
  const x = (event.clientX - rect.left) / rect.width;
  const y = (event.clientY - rect.top) / rect.height;
  return { x, y };
}

function drawAnnotation(ctx, ann, w, h) {
  ctx.strokeStyle = ann.color || '#e24b4a';
  ctx.fillStyle = ann.color || '#e24b4a';
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  if (ann.type === 'pen') {
    ctx.lineWidth = (ann.strokeWidth || 0.005) * w;
    ctx.beginPath();
    (ann.points || []).forEach((p, i) => {
      const x = p.x * w;
      const y = p.y * h;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
  } else if (ann.type === 'check') {
    const x = ann.x * w;
    const y = ann.y * h;
    const s = (ann.size || 0.04) * w;
    ctx.lineWidth = s * 0.16;
    ctx.beginPath();
    ctx.moveTo(x - s * 0.45, y);
    ctx.lineTo(x - s * 0.1, y + s * 0.35);
    ctx.lineTo(x + s * 0.55, y - s * 0.4);
    ctx.stroke();
  } else if (ann.type === 'x') {
    const x = ann.x * w;
    const y = ann.y * h;
    const s = (ann.size || 0.04) * w;
    ctx.lineWidth = s * 0.16;
    ctx.beginPath();
    ctx.moveTo(x - s * 0.4, y - s * 0.4);
    ctx.lineTo(x + s * 0.4, y + s * 0.4);
    ctx.moveTo(x + s * 0.4, y - s * 0.4);
    ctx.lineTo(x - s * 0.4, y + s * 0.4);
    ctx.stroke();
  } else if (ann.type === 'text') {
    const x = ann.x * w;
    const y = ann.y * h;
    const fs = (ann.fontSize || 0.025) * w;
    ctx.font = `${fs}px "Caveat", "Brush Script MT", cursive`;
    ctx.textBaseline = 'top';
    ctx.fillText(ann.text || '', x, y);
  }
}

function annotationBounds(ann) {
  if (ann.type === 'pen') {
    const xs = (ann.points || []).map((p) => p.x);
    const ys = (ann.points || []).map((p) => p.y);
    return {
      x1: Math.min(...xs),
      y1: Math.min(...ys),
      x2: Math.max(...xs),
      y2: Math.max(...ys),
    };
  }
  const size = ann.size || ann.fontSize || 0.04;
  return {
    x1: (ann.x || 0) - size / 2,
    y1: (ann.y || 0) - size / 2,
    x2: (ann.x || 0) + size / 2,
    y2: (ann.y || 0) + size / 2,
  };
}

function findNearest(annotations, x, y) {
  let best = null;
  let bestDist = Infinity;
  for (const ann of annotations) {
    const b = annotationBounds(ann);
    const cx = (b.x1 + b.x2) / 2;
    const cy = (b.y1 + b.y2) / 2;
    const d = (cx - x) ** 2 + (cy - y) ** 2;
    if (d < bestDist) {
      bestDist = d;
      best = ann;
    }
  }
  return best;
}

export default function AnnotationLayer({
  annotations,
  tool,
  color,
  onAdd,
  onRemove,
  width,
  height,
  readonly = false,
}) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !width || !height) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    for (const ann of annotations) drawAnnotation(ctx, ann, width, height);
    if (drawing) drawAnnotation(ctx, drawing, width, height);
  }, [annotations, drawing, width, height]);

  const handlePointerDown = useCallback(
    (e) => {
      if (readonly) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      const { x, y } = getRelativeCoords(e, canvasRef.current);

      if (tool === 'pen') {
        setDrawing({ type: 'pen', color, strokeWidth: 0.005, points: [{ x, y }] });
      } else if (tool === 'check' || tool === 'x') {
        onAdd({ type: tool, x, y, size: 0.04, color });
      } else if (tool === 'text') {
        const text = window.prompt('Texto de la anotación:');
        if (text && text.trim()) {
          onAdd({ type: 'text', x, y, text: text.trim(), fontSize: 0.028, color });
        }
      } else if (tool === 'eraser') {
        const nearest = findNearest(annotations, x, y);
        if (nearest && nearest.id) onRemove(nearest.id);
      }
    },
    [tool, color, annotations, onAdd, onRemove, readonly]
  );

  const handlePointerMove = useCallback(
    (e) => {
      if (!drawing) return;
      const { x, y } = getRelativeCoords(e, canvasRef.current);
      setDrawing((prev) =>
        prev ? { ...prev, points: [...prev.points, { x, y }] } : prev
      );
    },
    [drawing]
  );

  const handlePointerUp = useCallback(() => {
    if (drawing && drawing.points.length > 1) {
      onAdd(drawing);
    }
    setDrawing(null);
  }, [drawing, onAdd]);

  const cursor =
    readonly
      ? 'default'
      : tool === 'eraser'
      ? 'pointer'
      : tool === 'text'
      ? 'text'
      : 'crosshair';

  return (
    <canvas
      ref={canvasRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        cursor,
        touchAction: 'none',
        pointerEvents: readonly ? 'none' : 'auto',
      }}
    />
  );
}
